import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { useTranslation } from '../../../i18n/LanguageContext';
import translations from '../../../locales/translations';
import {
  TERMINOLOGY_KEYS, TERMINOLOGY_LANGUAGES,
  fetchOrgTerminology, saveOrgTerminology, fetchTerminology,
} from '../../../slice/terminologySlice';

const MAX_LENGTH = 60;
const GRID = 'grid grid-cols-[1.6fr_repeat(4,minmax(0,1fr))] items-center gap-4';

const toDraft = (terms) => {
  const draft = {};
  TERMINOLOGY_LANGUAGES.forEach((lang) => {
    draft[lang] = {};
    TERMINOLOGY_KEYS.forEach((key) => {
      draft[lang][key] = terms?.[lang]?.[key] || '';
    });
  });
  return draft;
};

const toPayload = (draft) => {
  const terms = {};
  TERMINOLOGY_LANGUAGES.forEach((lang) => {
    TERMINOLOGY_KEYS.forEach((key) => {
      const value = draft[lang][key].trim();
      if (value) (terms[lang] = terms[lang] || {})[key] = value;
    });
  });
  return terms;
};

const TerminologyTab = ({ organizationId, aispaceColor, addToast }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: userData } = useSelector((state) => state.admin);
  const [draft, setDraft] = useState(() => toDraft({}));
  const [saved, setSaved] = useState(() => toDraft({}));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    dispatch(fetchOrgTerminology(organizationId))
      .unwrap()
      .then((terms) => {
        const next = toDraft(terms);
        setDraft(next);
        setSaved(next);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (organizationId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const hasChanges = useMemo(() => JSON.stringify(draft) !== JSON.stringify(saved), [draft, saved]);

  const setValue = (lang, key, value) => {
    setDraft((prev) => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));
  };

  const handleSave = () => {
    setSaving(true);
    dispatch(saveOrgTerminology({ orgId: organizationId, terms: toPayload(draft) }))
      .unwrap()
      .then((terms) => {
        const next = toDraft(terms);
        setDraft(next);
        setSaved(next);
        if (String(userData?.organization_id || '').toLowerCase() === String(organizationId).toLowerCase()) {
          dispatch(fetchTerminology());
        }
        addToast(t('terminology.toastSaved'), 'success');
      })
      .catch((status) => {
        const key = status === 403 ? 'toastNoPermission' : status === 400 ? 'toastInvalid' : 'toastError';
        addToast(t(`terminology.${key}`), 'error');
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl">
      <div className="flex items-center justify-between gap-4 px-8 py-4">
        <p className="text-[14px] text-[#6B7280]">{t('terminology.hint')}</p>
        <div className="flex items-center gap-3 flex-shrink-0">
          {hasChanges && <span className="text-[14px] text-[#6B7280]">{t('terminology.unsavedChanges')}</span>}
          <button
            type="button"
            onClick={() => setDraft(saved)}
            disabled={!hasChanges || saving}
            className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('terminology.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="px-4 py-2 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center"
            style={{ backgroundColor: aispaceColor }}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : t('terminology.save')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 border-t border-[#E5E7EB]">
          <Loader2 size={22} className="animate-spin" style={{ color: aispaceColor }} />
        </div>
      ) : loadError ? (
        <div className="px-6 py-12 text-center border-t border-[#E5E7EB]">
          <p className="text-[14px] text-[#DC2626] mb-4">{t('terminology.loadError')}</p>
          <button
            type="button"
            onClick={load}
            className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
          >
            {t('terminology.retry')}
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className={`${GRID} px-8 py-3 border-t border-b border-[#E5E7EB] bg-[#F9FAFB]`}>
              <div className="text-[13px] font-medium text-[#6B7280]">{t('terminology.termColumn')}</div>
              {TERMINOLOGY_LANGUAGES.map((lang) => (
                <div key={lang} className="pl-3 text-[13px] font-medium text-[#6B7280] truncate">{t(`terminology.language.${lang}`)}</div>
              ))}
            </div>
            {TERMINOLOGY_KEYS.map((key, index) => (
              <div
                key={key}
                className={`${GRID} px-8 py-4 hover:bg-[#FAFAFA] transition-colors ${index !== TERMINOLOGY_KEYS.length - 1 ? 'border-b border-[#E5E7EB]' : ''}`}
              >
                <div className="text-[14px] font-medium text-[#111827]">{t(`terminology.term.${key}`)}</div>
                {TERMINOLOGY_LANGUAGES.map((lang) => (
                  <div key={lang} className="min-w-0">
                    <input
                      type="text"
                      value={draft[lang][key]}
                      maxLength={MAX_LENGTH}
                      disabled={saving}
                      onChange={(e) => setValue(lang, key, e.target.value.replace(/[{}<>]/g, ''))}
                      onFocus={(e) => { e.target.style.borderColor = aispaceColor; }}
                      onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; }}
                      placeholder={translations[lang]?.terminology?.defaults?.[key] || ''}
                      title={draft[lang][key] || translations[lang]?.terminology?.defaults?.[key] || ''}
                      className="w-full px-3 py-2.5 text-[14px] text-[#111827] bg-white border border-[#E5E7EB] rounded-lg placeholder-[#9CA3AF] focus:outline-none focus:ring-0 disabled:bg-[#F9FAFB]"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminologyTab;
